/**
 * Frontend mirrors of the L1.2 backend models for vessel photo +
 * documents. Numeric size_bytes is serialized as `number` by the
 * NestJS controller (BigInt → Number), so the SPA never has to
 * deal with bigint here.
 */

export const VESSEL_DOCUMENT_KINDS = [
  'GA',
  'DRYDOCK_PLAN',
  'STABILITY_BOOK',
  'SAFETY_PLAN',
  'MANUAL',
  'OTHER',
] as const;
export type VesselDocumentKind = (typeof VESSEL_DOCUMENT_KINDS)[number];

export interface VesselDocument {
  id: string;
  vesselId: string;
  kind: VesselDocumentKind;
  fileKey: string;
  fileName: string;
  contentType: string;
  /** Backend serializes BigInt → number. Capped at 50 MB (within
   *  safe-int range) by the upload guard. */
  sizeBytes: number;
  uploadedByUserId: string;
  uploadedAt: string;
  deletedAt: string | null;
}

/** Returned by every presign-upload endpoint. */
export interface PresignedUpload {
  url: string;
  fileKey: string;
  expiresIn: number;
}

/** Returned by `GET /vessels/:id/photo/url`. null when the vessel
 *  has no photo set. */
export interface VesselPhotoUrl {
  url: string;
  expiresIn: number;
}

/** Returned by `GET /vessels/:id/documents/:docId/download-url`. */
export interface VesselDocumentDownloadUrl {
  url: string;
  expiresIn: number;
  fileName: string;
}

/** Per-file caps enforced by the backend (mirrored client-side
 *  for instant feedback in the FileDropzone). */
export const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const DOC_MAX_BYTES = 50 * 1024 * 1024;
export const VESSEL_QUOTA_BYTES = 500 * 1024 * 1024;

export const ALLOWED_PHOTO_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export const ALLOWED_DOC_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
] as const;
